import * as z from "zod";
import {
  hsaReconciliationPageInputSchema,
  hsaReconciliationPaginationSchema,
} from "./hsa-reconciliation-common";

/** One row of Gale's Transactions table. Money is integer cents in
 * `currency`. The customer's email is deliberately not carried. */
export const hsaTransactionSchema = z.object({
  orderCode: z.string(),
  merchantReference: z.string().nullable(),
  /** The public checkout id (`cs_…`); null without a cart code. */
  checkoutId: z.string().nullable(),
  /** UTC ISO timestamp; Gale's dashboard renders it in Eastern time. */
  orderDate: z.string(),
  customerName: z.string().nullable(),
  /** `HSA_FSA` | `REGULAR` | `SPLIT` as Gale sends it. */
  cartType: z.string(),
  /** `PAID` | `PARTIALLY_REFUNDED` | `REFUNDED` … */
  status: z.string(),
  currency: z.string(),
  /** Which processor settled the order (`adyen` for every Redo merchant);
   * decides whether a missing payout is "pending" or "predates reporting". */
  processor: z.string().nullable(),
  /** Gale's own display strings, e.g. "Mastercard •••• 0174". */
  paymentMethods: z.array(z.string()),
  grossCents: z.number().int(),
  processingFeeCents: z.number().int(),
  lmnFeeCents: z.number().int(),
  totalFeesCents: z.number().int(),
  refundCents: z.number().int(),
  netToMerchantCents: z.number().int(),
  /** The payout that carried this order's capture, when reported. */
  payoutId: z.string().nullable(),
  payoutValueDate: z.string().nullable(),
});
export type HsaTransaction = z.infer<typeof hsaTransactionSchema>;

/**
 * Gale's reconciliation payments for the authenticated team, verbatim. Dates
 * are Eastern-time calendar days, as on Gale's dashboard. Beta:
 * Redo-employee-only; everyone else (and an unregistered team) gets the same
 * generic not-found.
 */
export const getHsaTransactions = {
  name: "getHsaTransactions" as const,
  input: hsaReconciliationPageInputSchema.extend({
    /** Order code, checkout id, receipt number, merchant reference or
     * customer; Gale rejects fewer than 2 characters. */
    search: z.string().min(2).optional(),
  }),
  output: z.object({
    transactions: z.array(hsaTransactionSchema),
    pagination: hsaReconciliationPaginationSchema,
  }),
};
